/**
 * Thin wrapper over the Google Photos Picker API.
 *
 * Endpoint and shapes per https://developers.google.com/photos/picker/reference/rest.
 * Field access is defensive — the spike's job is partly to find out where things actually live.
 */

const BASE = 'https://photospicker.googleapis.com/v1';

export interface PickerSession {
  id: string;
  pickerUri: string;
  mediaItemsSet?: boolean;
  /** Duration strings like "5s". */
  pollingConfig?: { pollInterval?: string; timeoutIn?: string };
  expireTime?: string;
}

export interface PickedMediaItem {
  id: string;
  createTime?: string;
  type?: string;
  mediaFile?: {
    baseUrl?: string;
    mimeType?: string;
    filename?: string;
    mediaFileMetadata?: { width?: number; height?: number };
  };
}

async function call<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`${response.status} ${response.statusText}${body ? ` — ${body.slice(0, 300)}` : ''}`);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function createSession(token: string): Promise<PickerSession> {
  return call<PickerSession>('/sessions', token, { method: 'POST', body: '{}' });
}

export function getSession(token: string, sessionId: string): Promise<PickerSession> {
  return call<PickerSession>(`/sessions/${encodeURIComponent(sessionId)}`, token);
}

export function deleteSession(token: string, sessionId: string): Promise<void> {
  return call<void>(`/sessions/${encodeURIComponent(sessionId)}`, token, { method: 'DELETE' });
}

export async function listMediaItems(token: string, sessionId: string): Promise<PickedMediaItem[]> {
  const items: PickedMediaItem[] = [];
  let pageToken: string | undefined;

  do {
    const params = new URLSearchParams({ sessionId, pageSize: '100' });
    if (pageToken) params.set('pageToken', pageToken);
    const page = await call<{ mediaItems?: PickedMediaItem[]; nextPageToken?: string }>(
      `/mediaItems?${params}`,
      token
    );
    if (page.mediaItems) items.push(...page.mediaItems);
    pageToken = page.nextPageToken;
  } while (pageToken);

  return items;
}

/** "5s" / "5.5s" → milliseconds. Falls back to 3s. */
export function parseDurationMs(duration: string | undefined, fallback = 3000): number {
  if (!duration) return fallback;
  const seconds = Number.parseFloat(duration.replace(/s$/, ''));
  return Number.isFinite(seconds) ? Math.max(seconds * 1000, 1000) : fallback;
}

/**
 * Append the size parameters the Picker API requires. A bare baseUrl is rejected — you must
 * supply either a download param or *both* max width and height.
 */
export function sizedBaseUrl(baseUrl: string, w: number, h: number): string {
  return `${baseUrl}=w${Math.round(w)}-h${Math.round(h)}`;
}
